import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Cloud, ArrowLeft, Mail, MessageSquare, Send, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulate form submission
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-[#1a1a2e]">
      {/* Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <Cloud className="w-8 h-8 text-[#39FF14]" />
            <span className="font-bold text-xl text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              FART BUTTON
            </span>
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-[#A0A0C0] hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="pt-32 pb-24 px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 
              className="text-4xl font-black text-white mb-4 text-center"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
            >
              Contact Us
            </h1>
            <p className="text-[#A0A0C0] text-center mb-12">
              Have questions, feedback, or just want to say hi? We'd love to hear from you!
            </p>

            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-[#2A2A4A] border border-[#39FF14]/30 rounded-3xl p-12 text-center"
              >
                <CheckCircle className="w-16 h-16 text-[#39FF14] mx-auto mb-6" />
                <h2 
                  className="text-2xl font-bold text-white mb-4"
                  style={{ fontFamily: 'Unbounded, sans-serif' }}
                >
                  Message Sent!
                </h2>
                <p className="text-[#A0A0C0] mb-8">
                  Thanks for reaching out! We'll get back to you as soon as possible.
                </p>
                <button
                  onClick={() => {
                    setSubmitted(false);
                    setFormData({ name: '', email: '', subject: '', message: '' });
                  }}
                  className="text-[#39FF14] hover:underline"
                >
                  Send another message
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white font-medium mb-2">
                      Your Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full bg-[#2A2A4A] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-[#A0A0C0] focus:outline-none focus:border-[#39FF14]/50 transition-colors"
                      placeholder="John Doe"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-white font-medium mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-[#2A2A4A] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-[#A0A0C0] focus:outline-none focus:border-[#39FF14]/50 transition-colors"
                      placeholder="john@example.com"
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    Subject
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#2A2A4A] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#39FF14]/50 transition-colors"
                    data-testid="contact-subject"
                  >
                    <option value="">Select a topic</option>
                    <option value="general">General Inquiry</option>
                    <option value="feedback">App Feedback</option>
                    <option value="bug">Bug Report</option>
                    <option value="feature">Feature Request</option>
                    <option value="business">Business Inquiry</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full bg-[#2A2A4A] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-[#A0A0C0] focus:outline-none focus:border-[#39FF14]/50 transition-colors resize-none"
                    placeholder="Tell us what's on your mind..."
                    data-testid="contact-message"
                  />
                </div>

                <button
                  type="submit"
                  className="cta-button w-full flex items-center justify-center gap-2"
                  data-testid="contact-submit"
                >
                  <Send className="w-5 h-5" />
                  Send Message
                </button>
              </form>
            )}

            {/* Alternative Contact Methods */}
            <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="bg-[#2A2A4A] border border-white/10 rounded-2xl p-6 text-center">
                <Mail className="w-10 h-10 text-[#39FF14] mx-auto mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">Email Us</h3>
                <a 
                  href="mailto:fartbutton.app@gmail.com" 
                  className="text-[#A0A0C0] hover:text-[#39FF14] transition-colors"
                >
                  fartbutton.app@gmail.com
                </a>
              </div>
              <div className="bg-[#2A2A4A] border border-white/10 rounded-2xl p-6 text-center">
                <MessageSquare className="w-10 h-10 text-[#FF00FF] mx-auto mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">Social Media</h3>
                <p className="text-[#A0A0C0]">
                  @fartbutton.app
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto text-center text-[#A0A0C0] text-sm">
          © 2026 Fart Button Madness. Every fart is unique!
        </div>
      </footer>
    </div>
  );
};

export default Contact;
